import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faBagShopping,
  faBed,
  faDumbbell,
  faLandmark,
  faMapPin,
  faMoonStars,
  faPalette,
  faSpa,
  faTree,
  faUtensils,
} from '@fortawesome/pro-duotone-svg-icons';
import type { AppCategory, PlaceCategory } from '@/types/itinerary';

export interface CategoryDefinition {
  label: string;
  icon: IconDefinition;
  pinColor: string;
  badgeClasses: { bg: string; text: string; border: string };
}

export const categoryDefinitions: Record<AppCategory, CategoryDefinition> = {
  dining: {
    label: 'Dining',
    icon: faUtensils,
    pinColor: '#e8a83a',
    badgeClasses: {
      bg: 'bg-tertiary-subtle',
      text: 'text-tertiary-emphasized',
      border: 'border-tertiary-main',
    },
  },
  outdoors: {
    label: 'Outdoors',
    icon: faTree,
    pinColor: '#4aa880',
    badgeClasses: {
      bg: 'bg-success-background',
      text: 'text-success-text',
      border: 'border-success-border',
    },
  },
  arts: {
    label: 'Arts & Culture',
    icon: faPalette,
    pinColor: '#c490e4',
    badgeClasses: {
      bg: 'bg-primary-subtle',
      text: 'text-primary-focal',
      border: 'border-primary-main',
    },
  },
  attractions: {
    label: 'Attractions',
    icon: faLandmark,
    pinColor: '#5898cc',
    badgeClasses: {
      bg: 'bg-info-background',
      text: 'text-info-text',
      border: 'border-info-border',
    },
  },
  nightlife: {
    label: 'Nightlife',
    icon: faMoonStars,
    pinColor: '#9b74e8',
    badgeClasses: {
      bg: 'bg-secondary-main/20',
      text: 'text-secondary-focal',
      border: 'border-secondary-main',
    },
  },
  sports: {
    label: 'Sports & Rec',
    icon: faDumbbell,
    pinColor: '#5dbdb0',
    badgeClasses: {
      bg: 'bg-quaternary-subtle',
      text: 'text-quaternary-emphasized',
      border: 'border-quaternary-main',
    },
  },
  shopping: {
    label: 'Shopping',
    icon: faBagShopping,
    pinColor: '#e87ba8',
    badgeClasses: {
      bg: 'bg-primary-subtle',
      text: 'text-primary-focal',
      border: 'border-primary-emphasized',
    },
  },
  wellness: {
    label: 'Wellness',
    icon: faSpa,
    pinColor: '#90bce4',
    badgeClasses: { bg: 'bg-info-badge', text: 'text-info-text', border: 'border-info-border' },
  },
  lodging: {
    label: 'Lodging',
    icon: faBed,
    pinColor: '#d0785a',
    badgeClasses: {
      bg: 'bg-primary-subtle',
      text: 'text-primary-foreground',
      border: 'border-primary-main',
    },
  },
  other: {
    label: 'Other',
    icon: faMapPin,
    pinColor: '#c0b0d8',
    badgeClasses: {
      bg: 'bg-content-focal',
      text: 'text-content-deemphasized',
      border: 'border-content-border',
    },
  },
};

/** Turn a provider subcategory into a display label. Most providers already
 *  supply a human name, but Overture passes through snake_case tokens like
 *  `seafood_restaurant`. Splits on `_` and title-cases each word; idempotent for
 *  already-human names (no underscores → existing capitals preserved). */
export const humanizeCategoryLabel = (raw: string): string =>
  raw
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

/** Drop categories that would render as the same tag, keeping the first.
 *
 *  A place matched across providers accumulates each provider's own category
 *  ids, so the same label arrives several times under different ids —
 *  ["Bakery", "bakery", "Bakery"] — and the ingest-side merge, which dedupes on
 *  the whole {id, name} object, can't see they're the same. Keying on the
 *  rendered label is what actually matches what the user sees.
 *
 *  Deduping matters beyond tidiness: callers cap the tag list (top 3), so
 *  repeats used to consume slots and hide real categories — ECHO showed
 *  "Museum" twice instead of surfacing "Tourist attraction". */
export const dedupeCategoriesByLabel = (categories: Array<PlaceCategory>): Array<PlaceCategory> => {
  const seen = new Set<string>();
  return categories.filter((category) => {
    const key = humanizeCategoryLabel(category.name).toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};
